import { lazy } from 'react';

/**
 * Drop-in replacement for React.lazy that survives deploys.
 *
 * When we ship a new build, Vite emits new hashed chunk filenames and Netlify
 * swaps them atomically. A browser tab still running the OLD index.html knows
 * only the OLD chunk hashes — so the first time that stale tab navigates to a
 * lazy route, the dynamic import 404s ("Failed to fetch dynamically imported
 * module") and the app would otherwise crash into the ErrorBoundary.
 *
 * Fix: on a chunk-load failure, reload the page ONCE (guarded by sessionStorage
 * so we can't loop) to pull the fresh index + current chunk hashes. The reload
 * is transparent — the user lands on the page they were navigating to. If the
 * import still fails right after a reload, it's a real error, so we rethrow and
 * let the ErrorBoundary handle it.
 */
const RELOAD_GUARD_KEY = 'chunkReloadAt';
const RELOAD_COOLDOWN_MS = 10_000;

const isChunkLoadError = (err) => {
  const msg = String(err?.message || err || '');
  return (
    /dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /Failed to fetch/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
};

export function lazyWithRetry(importer) {
  return lazy(() =>
    importer().catch((err) => {
      if (isChunkLoadError(err)) {
        let last = 0;
        try { last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0); } catch { /* ignore */ }
        // Only auto-reload if we haven't just done so — prevents a reload loop
        // if the chunk is genuinely missing (broken deploy).
        if (Date.now() - last > RELOAD_COOLDOWN_MS) {
          try { sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now())); } catch { /* ignore */ }
          window.location.reload();
          // Hang so React never renders an error frame before the reload lands.
          return new Promise(() => {});
        }
      }
      throw err;
    }),
  );
}
